"use client";

import React, { useState } from "react";
import { Phone, ArrowRight, ChevronDown, PhoneCall, ClipboardList, CalendarCheck, CircleCheck as CheckCircle2, CircleQuestionMark as HelpCircle, Clock, ShieldAlert } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Touch() {
  const prefersReducedMotion = useReducedMotion();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const steps = [{
    number: "01",
    icon: PhoneCall,
    title: "Call or Request Service",
    description: "Reach our 24/7 dispatch desk immediately by phone or complete our quick online service request form to begin."
  }, {
    number: "02",
    icon: ClipboardList,
    title: "Tell Us What's Happening",
    description: "Describe your symptoms—whether it is a leaking pipe, backed-up drain, or failing water heater—so we prepare the right diagnostic tools."
  }, {
    number: "03",
    icon: CalendarCheck,
    title: "Schedule the Appropriate Plumbing Service",
    description: "We coordinate rapid emergency dispatch or schedule an appointment with a licensed local plumbing technician in Jonesboro."
  }];
  const faqs = [{
    question: "What plumbing services do you provide in Jonesboro?",
    answer: "We provide comprehensive residential and commercial plumbing solutions, including 24/7 emergency repairs, leak detection, sewer and pipe repairs, drain cleaning, water heater maintenance, fixture installation, and whole-home repiping.",
    linkUrl: "/services/",
    linkText: "Explore our full range of Plumbing Services"
  }, {
    question: "What areas around Jonesboro do you serve?",
    answer: "Our service area covers Jonesboro, Clayton County, and surrounding communities including Morrow, Riverdale, Forest Park, Stockbridge, Ellenwood, and Lovejoy. Our dispatched trucks are fully stocked to service homes across the south metro area.",
    linkUrl: "/service-areas/",
    linkText: "View all covered Service Areas"
  }, {
    question: "How do you handle after-hours plumbing emergencies?",
    answer: "Plumbing emergencies cannot wait. We maintain an active 24/7 on-call dispatch team ready to respond to burst pipes, major sewage backups, gas water heater leaks, and severe flooding at any time of day or night.",
    linkUrl: "/contact/",
    linkText: "Contact 24/7 Emergency Dispatch"
  }, {
    question: "Do you repair and replace all types of water heaters?",
    answer: "Yes. We service conventional tank and high-efficiency tankless water heaters powered by gas or electricity across major brands such as Rheem, Rinnai, A.O. Smith, Navien, and Bradford White.",
    linkUrl: "/services/",
    linkText: "Learn more about Water Heater Solutions"
  }, {
    question: "How do you resolve severe clogged drains and sewer lines?",
    answer: "We use advanced diagnostic camera inspections paired with heavy-duty motorized cabling and hydro-jetting equipment to clear stubborn blockages, tree root intrusions, and grease build-up safely without damaging your pipes.",
    linkUrl: "/services/",
    linkText: "Read about Drain Cleaning Services"
  }, {
    question: "How do I request non-emergency plumbing service?",
    answer: "You can schedule a standard appointment or request an estimate by submitting our online service request form. We review requests promptly and confirm an appointment window that fits your schedule.",
    linkUrl: "/contact/",
    linkText: "Submit a Service Request Online"
  }, {
    question: "How can I contact Jonesboro Emergency Plumber directly?",
    answer: "For immediate dispatch or emergency questions, call our direct local line at (470) 847-9076. For routine inquiries, estimates, or non-urgent questions, visit our contact page to message our support team.",
    linkUrl: "/contact/",
    linkText: "Visit our Contact Page"
  }];
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  return <section id="how-it-works-faq" data-nav="light" className="relative bg-background py-14 md:py-20 lg:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-16 md:space-y-24">
        {/* Module 1: How Service Works */}
        <div>
          <div className="max-w-3xl mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1 text-xs sm:text-sm font-bold uppercase tracking-wider text-secondary-foreground mb-3">
              <span className="size-2 rounded-full bg-accent animate-pulse" />
              <span>Simple 3-Step Process</span>
            </div>
            <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
              How Our Plumbing Service Works
            </h2>
            <p className="font-default text-base sm:text-lg text-muted-foreground mt-3">
              From the initial call to completed repairs, we make getting professional plumbing help fast, clear, and hassle-free.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {steps.map((step, i) => {
            const IconComp = step.icon;
            return <motion.div key={step.number} data-index={i} initial={prefersReducedMotion ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.2
            }} transition={{
              duration: 0.35,
              delay: i * 0.1,
              ease: "easeOut"
            }} className="relative rounded-2xl bg-card border border-border p-6 sm:p-8 shadow-sm flex flex-col justify-between hover:border-primary/40 transition-[border-color,box-shadow] duration-300 hover:shadow-md">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex size-12 items-center justify-center rounded-xl bg-secondary text-primary">
                        <IconComp className="size-6" />
                      </div>
                      <span className="font-mono text-2xl sm:text-3xl font-bold text-accent">
                        {step.number}
                      </span>
                    </div>

                    <h3 className="font-default text-xl font-bold text-foreground pt-2">
                      {step.title}
                    </h3>

                    <p className="font-default text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  <div className="pt-6 mt-6 border-t border-border flex items-center text-xs font-semibold text-primary">
                    <CheckCircle2 className="size-4 mr-2 text-chart-4" />
                    <span>Clear Upfront Communication</span>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Module 2: FAQ Accordion */}
        <div className="pt-4">
          <div className="max-w-3xl mb-8 md:mb-12">
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1 text-xs sm:text-sm font-bold uppercase tracking-wider text-secondary-foreground mb-3">
              <HelpCircle className="size-3.5 text-primary" />
              <span>Frequently Asked Questions</span>
            </div>
            <h2 className="font-default text-3xl sm:text-4xl font-bold tracking-tight text-foreground leading-tight">
              Plumbing Questions & Answers
            </h2>
            <p className="font-default text-base sm:text-lg text-muted-foreground mt-2">
              Find fast answers regarding our coverage, emergency protocols, service scopes, and how we handle local repairs.
            </p>
          </div>

          <div className="space-y-3.5">
            {faqs.map((faq, i) => {
            const isOpen = openFaqIndex === i;
            return <div key={faq.question} data-index={i} className="rounded-xl border border-border bg-card overflow-hidden transition-[border-color] duration-200 hover:border-primary/40 shadow-xs">
                  <Button type="button" variant="ghost" onClick={() => toggleFaq(i)} aria-expanded={isOpen} className="w-full h-auto flex items-center justify-between p-5 sm:p-6 text-left cursor-pointer transition-colors duration-200 hover:bg-muted/40 rounded-none whitespace-normal justify-between">
                    <span className="font-default font-semibold text-base sm:text-lg text-foreground pr-4 text-left">
                      {faq.question}
                    </span>
                    <ChevronDown className={`size-5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180 text-primary" : ""}`} />
                  </Button>

                  <AnimatePresence initial={false}>
                    {isOpen && <motion.div initial={prefersReducedMotion ? {
                  opacity: 1,
                  height: "auto"
                } : {
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: "auto"
                }} exit={prefersReducedMotion ? {
                  opacity: 0,
                  height: 0
                } : {
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25,
                  ease: [0.16, 1, 0.3, 1]
                }}>
                        <div className="px-5 pb-5 sm:px-6 sm:pb-6 pt-0 border-t border-border/60">
                          <p className="font-default text-sm sm:text-base text-muted-foreground leading-relaxed pt-3">
                            {faq.answer}
                          </p>
                          <div className="pt-3">
                            <Link to={faq.linkUrl} className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-primary hover:underline hover:text-primary/80">
                              <span>{faq.linkText}</span>
                              <ArrowRight className="size-3.5" />
                            </Link>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>

        {/* Module 3: Final CTA Navy Panel */}
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.35,
        ease: "easeOut"
      }} className="relative overflow-hidden rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 md:p-14 lg:p-16 shadow-xl">
          {/* Subtle emergency gold triage accent indicator */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-accent md:hidden" />
          <div className="hidden md:block absolute top-0 left-0 bottom-0 w-2.5 bg-accent" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* CTA Left Content */}
            <div className="lg:col-span-7 space-y-4 md:pl-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1 text-xs sm:text-sm font-bold uppercase tracking-wider text-accent border border-white/15">
                <ShieldAlert className="size-3.5" />
                <span>Immediate 24/7 Support</span>
              </div>

              <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
                Have a Plumbing Problem?
              </h2>

              <p className="font-default text-base sm:text-lg text-primary-foreground/90 max-w-2xl leading-relaxed">
                Describe your issue and let our licensed Jonesboro technicians diagnose, repair, and restore your plumbing systems with upfront pricing and zero delay.
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-4 text-xs sm:text-sm text-primary-foreground/80">
                <div className="flex items-center gap-1.5">
                  <Clock className="size-4 text-accent" />
                  <span>Average Response Under 60 Mins</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-chart-4" />
                  <span>Licensed & Fully Insured</span>
                </div>
              </div>
            </div>

            {/* CTA Right Actions */}
            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3.5 justify-center lg:items-end">
              {/* Call Now Button */}
              <Button asChild className="w-full sm:w-auto lg:w-full h-auto py-4 px-6 rounded-full bg-accent text-accent-foreground font-bold shadow-md hover:brightness-105 transition-[filter,box-shadow] duration-200">
                <a href="tel:4708479076" className="flex items-center justify-center gap-3 text-base sm:text-lg">
                  <Phone className="size-5 shrink-0" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              {/* Request Service Button */}
              <Button asChild variant="outline" className="w-full sm:w-auto lg:w-full h-auto py-4 px-6 rounded-full bg-background text-primary font-semibold border-white/20 shadow-xs hover:bg-muted transition-colors duration-200">
                <Link to="/contact/" className="flex items-center justify-center gap-2 text-base">
                  <span>Request Service</span>
                  <ArrowRight className="size-4 shrink-0" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}